import { Input, type Theme } from '@mui/material';
import { useState } from 'react';
import { ComponentBox } from '@/admins/SmartReports/components/Components/Common/ComponentBox';
import { ComponentTitle } from '@/admins/SmartReports/components/Components/Common/ComponentTitle';
import { LoadingErrorWrapper } from '@/admins/SmartReports/components/Components/Common/LoadingErrorWrapper';
import { TextAreaThumbnail } from '@/admins/SmartReports/components/Components/TextFields/TextArea/TextArea';
import { SmartReportComponentTypesEnum, SmartReportTypeEnum } from '@/admins/SmartReports/types';
import type { ComponentDefinition, ComponentEditProps, ComponentViewProps } from '../types';
import { TextAreaView } from './TextArea/TextAreaView';
import type { TextAreaData, TextAreaEditProps, TextAreaSection } from './TextArea/types';

export const COMPONENT_ID = 'text_area_with_header';

type TextAreaWithHeaderData = {
  header: string;
} & TextAreaData;

export type TextAreaWithHeaderViewProps = {
  onHeaderChange?: ((value: string) => void) | null;
} & ComponentViewProps<TextAreaWithHeaderData> &
  TextAreaEditProps;

const styles = {
  header: {
    fontSize: 'inherit',
    fontWeight: 'inherit',
    color: 'inherit',
    fontFamily: 'inherit',
    border: 'none',
    outline: 'none',
    background: 'transparent',

    ':hover': { background: (theme: Theme) => theme.palette.action.hover },
  },
  headerInput: {
    height: '1em',
    textAlign: 'center',
    padding: 0,
  },
};

const TextAreaWithHeaderView = ({
  sx,
  data,
  onHeaderChange,
  onAddSection,
  onRemoveSection,
  onUpdateSection,
  isEdit = false,
}: TextAreaWithHeaderViewProps) => {
  return (
    <ComponentBox>
      <ComponentTitle>
        {isEdit ? (
          <Input
            type='text'
            fullWidth
            disableUnderline
            placeholder='Enter header...'
            sx={styles.header}
            inputProps={{ sx: styles.headerInput }}
            value={data?.header}
            onChange={e => onHeaderChange?.(e.target.value)}
          />
        ) : (
          data?.header
        )}
      </ComponentTitle>

      <div style={{ flex: 1 }}>
        <TextAreaView
          sx={sx || {}}
          data={data as TextAreaData}
          onAddSection={onAddSection ?? null}
          onRemoveSection={onRemoveSection ?? null}
          onUpdateSection={onUpdateSection ?? null}
          isEdit={isEdit}
        />
      </div>
    </ComponentBox>
  );
};

const TextAreaWithHeader = ({
  persistedData,
  onComponentLoad,
  sx,
}: ComponentEditProps<TextAreaWithHeaderData>) => {
  const [data, setData] = useState<TextAreaWithHeaderData>(
    persistedData || { header: '', sections: [{ title: '', content: '' }] },
  );

  // Helper function to call onComponentLoad with consistent data structure
  const notifyComponentLoad = (newData: TextAreaWithHeaderData): void => {
    if (onComponentLoad && newData.sections && newData.sections.length > 0) {
      onComponentLoad({
        componentId: COMPONENT_ID,
        data: newData,
      });
    }
  };

  const onHeaderChange = (value: string): void => {
    const newData = { ...data, header: value };
    setData(newData);
    notifyComponentLoad(newData);
  };

  const addSection = (): void => {
    if (data.sections.length < 4) {
      const newSections = [...data.sections, { title: '', content: '' }];
      const newData = { ...data, sections: newSections };
      setData(newData);
      notifyComponentLoad(newData);
    }
  };

  const removeSection = (index: number): void => {
    if (data.sections.length > 1) {
      const newSections = data.sections.filter((_, i) => i !== index);
      const newData = { ...data, sections: newSections };
      setData(newData);
      notifyComponentLoad(newData);
    }
  };

  const updateSection = (index: number, field: keyof TextAreaSection, value: string): void => {
    const newSections = [...data.sections];
    newSections[index] = { ...newSections[index], [field]: value } as TextAreaSection;
    const newData = { ...data, sections: newSections };
    setData(newData);
    notifyComponentLoad(newData);
  };

  return (
    <LoadingErrorWrapper loading={false} error={null} data={data}>
      <TextAreaWithHeaderView
        sx={sx}
        data={data}
        onHeaderChange={onHeaderChange}
        onAddSection={addSection}
        onRemoveSection={removeSection}
        onUpdateSection={updateSection}
        isEdit={true}
      />
    </LoadingErrorWrapper>
  );
};

// Thumbnail version for sidebar - no editing functionality
const TextAreaWithHeaderThumbnail = ({ sx }: ComponentEditProps<TextAreaWithHeaderData>) => {
  return (
    <ComponentBox>
      <ComponentTitle sx={{ fontSize: '0.5rem', marginBottom: 0 }}>
        Text Area With Header
      </ComponentTitle>
      <TextAreaThumbnail sx={sx} portfolio={{}} />
    </ComponentBox>
  );
};

export const TextAreaWithHeaderComponent: ComponentDefinition<TextAreaWithHeaderData> = {
  id: COMPONENT_ID,
  name: 'Text Area w/ Header',
  type: SmartReportComponentTypesEnum.TextArea,
  reportTypes: Object.values(SmartReportTypeEnum),
  Thumbnail: TextAreaWithHeaderThumbnail,
  Component: TextAreaWithHeader,
  ComponentView: TextAreaWithHeaderView,
};
